import { WillMyCreditsTransferState } from '@/store/wmct/state';
import { IWmctMapItem,
	IWmctEquivalency,
	IWmctEquivalentCourse,
	IWillMyCreditsTransferRequest,
	IAssessmentProvider,
	ITakenCourse,
	IWmctTakenCourse} from '@/models/services/collegetransfer/entities';
import CourseEntry from '@/models/CourseEntry';
import config from '@/models/services/config';
import { IInstitutionInfo, ICourseInfo, AssessmentEntry } from '@/models';

const getters = {
	isDuplicateCoursesOnSource(wmctState: WillMyCreditsTransferState): boolean {
		const equivMap = wmctState.equivalencyMap;
		if (equivMap == null) {
			return false;
		}

		const transferMap = equivMap.TransferMap;
		if (transferMap == null || transferMap.length === 0) {
			return false;
		}

		const equivalencies = transferMap.reduce((equivs: IWmctEquivalency[], mapItem: IWmctMapItem) => {
			equivs.push(...mapItem.Equivalencies);
			return equivs;
		}, []);

		const equivSources = equivalencies.reduce((sources: IWmctTakenCourse[], equiv: IWmctEquivalency) => {
			sources.push(...equiv.TakenCourses);
			return sources;
		}, []);

		// CourseKey is only returned back when it's there's a match (i.e., coming from EquivSource)
		const courseIds = equivSources.map((takenCourse: IWmctTakenCourse) =>
			`${takenCourse.UnitId}-${takenCourse.CoursePrefix}-${takenCourse.CourseNumber}-${takenCourse.GradeReceived}`);

		return Array.from(new Set(courseIds)).length !== courseIds.length;
	},

	isDuplicateCoursesOnTarget(wmctState: WillMyCreditsTransferState): boolean {
		const equivMap = wmctState.equivalencyMap;
		if (equivMap == null) {
			return false;
		}

		const transferMap = equivMap.TransferMap;
		if (transferMap == null || transferMap.length === 0) {
			return false;
		}

		const equivalencies = transferMap.reduce((equivs: IWmctEquivalency[], mapItem: IWmctMapItem) => {
			equivs.push(...mapItem.Equivalencies);
			return equivs;
		}, []);

		const equivTargets = equivalencies.reduce((targets: IWmctEquivalentCourse[], equiv: IWmctEquivalency) => {
			targets.push(...equiv.EquivalentCourses);
			return targets;
		}, []);

		const courseIds = equivTargets.map((equivalentCourse: IWmctEquivalentCourse) => equivalentCourse.InternalCourseId);

		return Array.from(new Set(courseIds)).length !== courseIds.length;
	},

	getTotalCreditsForTargetSchool(wmctState: WillMyCreditsTransferState): (val: IWmctMapItem) => number {
		return (val: IWmctMapItem) => {
			const whereIsCreditGrantingStatus = getters.getIsCreditGrantingStatus(wmctState);
			const equivs = val.Equivalencies.filter(whereIsCreditGrantingStatus);

			return parseFloat(equivs.reduce((schoolCredits: number, equiv: IWmctEquivalency) =>
				schoolCredits += equiv.EquivalentCourses.reduce((credits: number, course: IWmctEquivalentCourse) =>
					credits += course.Credits, 0), 0).toFixed(2));
		};
	},

	getTotalCoursesForTargetSchool(wmctState: WillMyCreditsTransferState): (val: IWmctMapItem) => number {
		return (val: IWmctMapItem) => {
			const whereIsCreditGrantingStatus = getters.getIsCreditGrantingStatus(wmctState);
			const equivs = val.Equivalencies.filter(whereIsCreditGrantingStatus);

			return equivs.reduce((schoolCourses: number, equiv: IWmctEquivalency) =>
				schoolCourses += equiv.EquivalentCourses.reduce((equivCourses: number, course: IWmctEquivalentCourse) =>
					equivCourses += 1, 0), 0);
		};
	},

	getIsCreditGrantingStatus(wmctState: WillMyCreditsTransferState): (val: IWmctEquivalency) => boolean {
		return (val: IWmctEquivalency) => val.StatusId === 0 // MeetsCriteria
			|| val.StatusId === 1 // MeetsCriteriaOptimistic
			|| val.StatusId === 6; // NoGradeEntered
	},

	getStatusIcon(wmctState: WillMyCreditsTransferState): (val: IWmctEquivalency) => string | null {
		return (val: IWmctEquivalency) => {
			switch (val.StatusId) {
				case 0: // MeetsCriteria
				case 1: // MeetsCriteriaOptimistic
				case 6: // NoGradeEntered
					return 'check';
				case 3: // UndeterminedMinGradeNotRecognized
				case 4: // UndeterminedStudentGradeNotRecognized
				case 5: // UndeterminedStudentAndMinGradesNotRecognized
					return null;
				case 2: // DoesNotMeetCriteria
				case 7: // DoesNotTransfer
					return 'close';
				default:
					return null;
			}
		};
	},

	getStatusText(wmctState: WillMyCreditsTransferState): (mapItem: IWmctMapItem, val: IWmctEquivalency) => string  {
		return (mapItem: IWmctMapItem, val: IWmctEquivalency) => {
			const minGrade = val.TakenCourses[0].MinimumGradeRequirement;
			const target = mapItem.ToSchool.InstitutionName;
			const enteredGrade = val.TakenCourses.length === 1 ? val.TakenCourses[0].GradeEntered
				: null;

			switch (val.StatusId) {
				case 0: // MeetsCriteria
					return '';
				case 1: // MeetsCriteriaOptimistic
					return 'Final determination may take into account taking the same course more than once.';
				case 2: // DoesNotMeetCriteria
					return 'Your course may not transfer due to unmet minimum grade requirements.'
						+ (minGrade ? ` Min Grade: ${minGrade}` : '');
				case 3: // UndeterminedMinGradeNotRecognized
				case 5: // UndeterminedStudentAndMinGradesNotRecognized
					return `${target}'s minimum grade requirement is unknown.`;
				case 4: // UndeterminedStudentGradeNotRecognized
					return `The grade (${enteredGrade}) is unrecognized.`;
				case 6: // NoGradeEntered
					return `Assumes minimum grade (${minGrade}) or higher is earned.`;
				case 7: // DoesNotTransfer
					return 'Does not transfer.';
				default:
					return '<undefined status>';
			}
		};
	},

	getUnseenEquivalencyCount(wmctState: WillMyCreditsTransferState): number {
		return getters.getAllEquivalencyCount(wmctState) - wmctState.seenEquivalencies;
	},

	getTermName(wmctState: WillMyCreditsTransferState): (val: number) => string {
		return (val: number) => {
			switch (val) {
				case 1:
					return 'Spring';
				case 2:
					return 'Summer';
				case 3:
					return 'Fall';
				case 4:
					return 'Winter';
			}

			return 'Unknown';
		};
	},

	getAllEquivalencyCount(wmctState: WillMyCreditsTransferState): number {
		const wmct = wmctState.equivalencyMap;

		if (!wmct) {
			return 0;
		}

		if (wmct.TransferMap == null) {
			return 0;
		}

		return wmct.TransferMap.reduce((count, mapItem) =>
			count + mapItem.Equivalencies.reduce((count2, equiv) =>
				count2 + equiv.EquivalentCourses.length, 0), 0);
	},

	isAddingCourse(wmctState: WillMyCreditsTransferState): boolean {
		return wmctState.courses.some((c) => c.id === 0);
	},

	isEditingCourse(wmctState: WillMyCreditsTransferState): boolean {
		return wmctState.courses.some((c) => c.isEditing);
	},

	isAddingAssessment(wmctState: WillMyCreditsTransferState): boolean {
		return wmctState.assessments.some((c) => c.id === 0);
	},

	isEditingAssessment(wmctState: WillMyCreditsTransferState): boolean {
		return wmctState.assessments.some((c) => c.isEditing);
	},

	isEditingAny(wmctState: WillMyCreditsTransferState): boolean {
		return getters.isAddingCourse(wmctState)
			|| getters.isAddingAssessment(wmctState)
			|| getters.isEditingCourse(wmctState)
			|| getters.isEditingAssessment(wmctState);
	},

	assessmentProviderOptions(wmctState: WillMyCreditsTransferState): IInstitutionInfo[] {
		const apiData = wmctState.api.data;
		if (apiData && apiData.providers) {
			const providers: IInstitutionInfo[] = apiData.providers.map((provider: IAssessmentProvider) => ({
				logo: provider.Logo,
				label: provider.InstitutionName,
				value: provider.UnitId,
				institutionId: provider.InstitutionId,
				scorePolicy: provider.ScorePolicy,
			}));

			return providers;
		}

		return [];
	},

	getAssessmentOptions(wmctState: WillMyCreditsTransferState): (val: IInstitutionInfo) => ICourseInfo[] {
		return (val: IInstitutionInfo) => {
			const apiData = wmctState.api.data;
			if (!apiData || !apiData.providers) {
				return [];
			}

			const providers = apiData.providers
				.filter((provider) => provider.InstitutionId === val.institutionId);

			if (!providers.length) {
				return [];
			}

			const assessments = providers[0].Assessments.map((assessment) => ({
				label: assessment.Title,
				value: assessment.CourseId,
				prefix: assessment.Prefix,
				number: assessment.Number,
				institutionId: assessment.InstitutionId,
			}));

			return assessments;
		};
	},

	equivalencyRequestPayload(wmctState: WillMyCreditsTransferState): IWillMyCreditsTransferRequest {
		const courses: ITakenCourse[] = wmctState.courses
			.filter((c: CourseEntry) => c.id)
			.map((c: CourseEntry) => {
				return {
					UnitId: c.institution.value as string,
					Prefix: c.course.prefix,
					Number: c.course.number,
					Outcome: c.grade,
					AcademicTerm: c.academicTerm as number,
					AcademicYear: c.academicYear as number,
				};
			});

		const assessments: ITakenCourse[] = wmctState.assessments
			.filter((c: AssessmentEntry) => c.id)
			.map((c: AssessmentEntry) => {
				return {
					UnitId: c.institution.value as string,
					Prefix: c.course.prefix,
					Number: c.course.number,
					Outcome: c.score,
					AcademicYear: c.academicYear as number,
				};
			});

		return {
			TargetInstitutions: [config.TargetUnitId],
			TakenCourses: courses.concat(assessments),
		};
	},

	clientInstitutionName(wmctState: WillMyCreditsTransferState) {
		if (wmctState.api.data) {
			return wmctState.api.data.institutionName;
		}

		return null;
	},
};

export default getters;
